import {
    getAccessIdFromAccess,
    getBillingAccountIdFromAccess,
    getIndividualIdFromAccess,
    getIndividualIdFromAccount,
    getProductFromContextProducts,
    getProductFromInteraction,
    getRealizingServiceByName,
    IBillingAccount,
    INTERACTION_TYPES,
    INTERACTION_TYPES_REGEX,
    IResource,
    OneXInteractionObject,
    OneXInteractionObjectContext,
    RequestStatusType,
    ShellStore,
    TreeNode
} from '@vfmfe/foundations';
import { getContextualization, getContextualizationTree } from '../api';
import { addInformationToInteraction, closeInteraction, setCurrentInteraction } from '../redux/reducers/interactions';
import { mapNodeType } from '../api/customerTree';
import { mapTreeNode } from '../api/customerTree/mapper/tree';
import { dispatchNotification } from '../hooks/useNotifications';
import { INTERACTION_TYPES_ENTITLEMENT } from '../config/interaction';

export const mapResponseToInteraction = (
    data: OneXInteractionObjectContext,
    searchType: INTERACTION_TYPES
): OneXInteractionObject => {
    const interactionObject: OneXInteractionObject = {
        instance_id: 'N/A',
        identifier: 'N/A',
        shortIdentifier: 'N/A',
        label: 'N/A',
        context: data,
        contextType: searchType,
        individualRequestState: RequestStatusType.IDLE,
        billingAccountRequestState: RequestStatusType.IDLE,
        accessRequestState: RequestStatusType.IDLE,
        id: new Date().getTime(),
        treeRequestState: RequestStatusType.IDLE,
        personalizedOffers: { status: RequestStatusType.IDLE }
    };
    switch (searchType) {
        case INTERACTION_TYPES.INDIVIDUAL:
            interactionObject.identifier = data?.individual?.id || 'N/A';
            interactionObject.shortIdentifier = data?.individual?.id || 'N/A';
            interactionObject.label = data?.individual?.id || 'N/A';
            return interactionObject;
        case INTERACTION_TYPES.BILLING_ACCOUNT:
            interactionObject.identifier = data?.billingAccount?.id || 'N/A';
            interactionObject.shortIdentifier = data?.billingAccount?.id || 'N/A';
            interactionObject.label = data?.billingAccount?.id || 'N/A';
            return interactionObject;
        case INTERACTION_TYPES.PRODUCT:
            const serviceId = getRealizingServiceByName(getProductFromContextProducts(data?.product), 'ServiceId');
            interactionObject.identifier = serviceId || 'N/A';
            interactionObject.shortIdentifier = serviceId || 'N/A';
            interactionObject.label = serviceId || 'N/A';
            return interactionObject;
        case INTERACTION_TYPES.ACCESS:
            const accessId = getAccessIdFromAccess(data?.resource);
            interactionObject.identifier = accessId;
            interactionObject.shortIdentifier = accessId;
            interactionObject.label = accessId;
            return interactionObject;
        default:
            throw new Error('Invalid search type');
    }
};

export const createInteraction = async (type: INTERACTION_TYPES, id: string): Promise<OneXInteractionObject> => {
    const getContextualizationRes = await getContextualization({ type, id });
    const interaction = mapResponseToInteraction(getContextualizationRes.data as OneXInteractionObjectContext, type); //TODO Review this type casting

    return interaction;
};

export const updateInteractionObject = (
    store: ShellStore,
    interactionUpdates?: Partial<OneXInteractionObject>,
    contextUpdates?: Partial<OneXInteractionObjectContext>
): void => {
    const currentInteraction = store.getState().interactions.currentInteraction;
    const newInteractionObject: OneXInteractionObject = interactionUpdates
        ? { ...currentInteraction, ...interactionUpdates }
        : { ...currentInteraction };
    if (contextUpdates) {
        newInteractionObject.context = {
            ...currentInteraction.context,
            ...contextUpdates
        };
    }
    store.dispatch(addInformationToInteraction(newInteractionObject));
};

export const recontextualizeInteraction = async (store: ShellStore, node: TreeNode) => {
    const contextType = mapNodeType(node.type || '');
    const identifier = node.id;

    try {
        if (!contextType || !identifier) throw new Error('Interaction not found');

        // validate if there are any open journeys that are dirty
        await recontextInteraction({ type: contextType, identifier, store });
    } catch (error) {
        console.error('Error during recontextualization:', error);
        dispatchNotification({ message: (error as Error).message, severity: 'error' });
    }
};

export const isValidContextualization = (
    contextType: INTERACTION_TYPES,
    searchValue: string,
    userEntitlements: string[]
): boolean => {
    if (!INTERACTION_TYPES_REGEX[contextType]?.searchTextField?.test(searchValue)) {
        return false;
    }
    const necessaryEntitlement = INTERACTION_TYPES_ENTITLEMENT[contextType as keyof typeof INTERACTION_TYPES];
    if (necessaryEntitlement) {
        const hasEntitlement = userEntitlements.includes(necessaryEntitlement);
        if (!hasEntitlement) {
            return false;
        }
    }
    return true;
};

export const recontextInteraction = async ({
    identifier,
    type,
    store
}: {
    type: string;
    identifier: string;
    store: ShellStore;
}) => {
    const userEntitlements = store.getState().authorization.entitlements;
    if (!type || !identifier) throw new Error('Interaction not found');
    const contextType = INTERACTION_TYPES[type?.toUpperCase() as keyof typeof INTERACTION_TYPES];
    if (isValidContextualization(contextType, identifier, userEntitlements)) {
        // TODO - validate if we need to emit event on start
        try {
            const res = await getContextualization({ id: identifier, type: contextType });
            const interaction = mapResponseToInteraction(res?.data as OneXInteractionObjectContext, contextType);

            if (!interaction) {
                throw new Error('Interaction not found');
            }
            store.dispatch(closeInteraction());
            store.dispatch(setCurrentInteraction(interaction));
            await enrichContextualizationCompleted(store);
            loadTreeData(store, { type: interaction.contextType, id: interaction.identifier });
        } catch (error) {
            console.error('Error fetching contextualization:', error);
            throw error;
        } finally {
            // TODO - validate if we need to emit event on end
        }
    } else {
        console.error('Invalid contextualization');
    }
};

export const enrichContextualizationCompleted = async (store: ShellStore): Promise<void> => {
    const interaction: OneXInteractionObject = store.getState()?.interactions?.currentInteraction;
    if (!interaction?.contextType || !interaction?.identifier) {
        throw new Error('Interaction not found');
    }

    const ensureIndividual = async () => {
        const latestInteraction: OneXInteractionObject = store.getState()?.interactions?.currentInteraction;
        if (!latestInteraction?.context?.individual) {
            const loaded = await loadIndividualData(store);
            if (!loaded) {
                throw new Error('Individual error');
            }
        }
    };

    switch (interaction.contextType) {
        case INTERACTION_TYPES.INDIVIDUAL:
            break;
        case INTERACTION_TYPES.BILLING_ACCOUNT:
            await ensureIndividual();
            break;
        case INTERACTION_TYPES.PRODUCT:
            await loadAccessData(store);

            await ensureIndividual();
            break;
        case INTERACTION_TYPES.ACCESS:
            if (!(await loadBillingAccountData(store))) {
                throw new Error('Access Error');
            }

            await ensureIndividual();
            break;
        default:
            throw new Error('Contextualization Error');
    }
};

export const loadIndividualData = async (store: ShellStore): Promise<boolean> => {
    const currentInteraction = store.getState()?.interactions?.currentInteraction;
    const { individualId: accountIndividualId, isAnonymous: isIndividualAnonymous } = getIndividualIdFromAccount(
        currentInteraction?.context?.billingAccount
    );
    const accessIndividualId = getIndividualIdFromAccess(currentInteraction?.context?.resource);
    const individualId = accountIndividualId || accessIndividualId;
    if (individualId) {
        if (isIndividualAnonymous) {
            updateInteractionObject(store, {
                individualRequestState: RequestStatusType.CANCELLED
            });
            return true;
        } else {
            updateInteractionObject(store, {
                individualRequestState: RequestStatusType.LOADING
            });
            try {
                const individual = (
                    await getContextualization({ id: individualId, type: INTERACTION_TYPES.INDIVIDUAL })
                ).data.individual as OneXInteractionObjectContext['individual'];

                updateInteractionObject(
                    store,
                    {
                        individualRequestState: RequestStatusType.SUCCESS
                    },
                    { individual }
                );
                return true;
            } catch (error) {
                updateInteractionObject(store, {
                    individualRequestState: RequestStatusType.ERROR
                });
                console.error('Error loading individual data', error);
                return false;
            }
        }
    } else {
        console.error('Error loading individual data - related party id not found');
        return false;
    }
};

export const loadAccessData = async (store: ShellStore): Promise<boolean> => {
    const currentInteraction = store.getState()?.interactions?.currentInteraction;
    const accessId = getRealizingServiceByName(getProductFromInteraction(currentInteraction), 'AccessId');
    if (accessId) {
        updateInteractionObject(store, {
            accessRequestState: RequestStatusType.LOADING
        });
        try {
            const accessData = (await getContextualization({ id: accessId, type: INTERACTION_TYPES.ACCESS })).data;

            let interactionResource = currentInteraction?.context?.resource || [];
            if (accessData?.resource && Array.isArray(accessData.resource)) {
                if (interactionResource.length > 0) {
                    interactionResource = [accessData.resource[0] as IResource, ...interactionResource];
                } else {
                    interactionResource = accessData.resource as IResource[];
                }
            }
            updateInteractionObject(
                store,
                {
                    accessRequestState: RequestStatusType.SUCCESS
                },
                { resource: interactionResource }
            );
            return true;
        } catch (error) {
            updateInteractionObject(store, {
                accessRequestState: RequestStatusType.ERROR
            });
            console.error('Error loading access data', error);
            return false;
        }
    }
    return false;
};

export const loadBillingAccountData = async (store: ShellStore): Promise<boolean> => {
    const currentInteraction = store.getState()?.interactions?.currentInteraction;
    const billingAccountId = getBillingAccountIdFromAccess(currentInteraction?.context?.resource);
    if (billingAccountId) {
        updateInteractionObject(store, {
            billingAccountRequestState: RequestStatusType.LOADING
        });
        try {
            const billingAccount = (
                await getContextualization({ id: billingAccountId, type: INTERACTION_TYPES.BILLING_ACCOUNT })
            ).data.billingAccount as IBillingAccount;

            updateInteractionObject(
                store,
                {
                    billingAccountRequestState: RequestStatusType.SUCCESS
                },
                { billingAccount }
            );
            return true;
        } catch (error) {
            updateInteractionObject(store, {
                billingAccountRequestState: RequestStatusType.ERROR
            });
            console.error('Error loading billing account data', error);
            return false;
        }
    }
    return false;
};

export const loadTreeData = async (store: ShellStore, node: TreeNode) => {
    updateInteractionObject(store, {
        treeRequestState: RequestStatusType.LOADING
    });
    const nodeType = mapNodeType(node.type || '');
    try {
        const tree = (await getContextualizationTree({ id: node.id, type: nodeType || '' })).data;

        if (!tree) {
            updateInteractionObject(
                store,
                {
                    treeRequestState: RequestStatusType.CANCELLED
                },
                {
                    tree: {
                        ...store.getState().interactions.currentInteraction?.context?.tree,
                        currentNodeId: node.id,
                        currentNodeType: nodeType
                    }
                }
            );
        }
        updateInteractionObject(
            store,
            {
                treeRequestState: RequestStatusType.SUCCESS
            },
            {
                tree: {
                    nodes: mapTreeNode(
                        tree,
                        store.getState().interactions.currentInteraction?.context?.tree?.nodes,
                        node.id
                    ),
                    currentNodeId: node.id,
                    currentNodeType: nodeType
                }
            }
        );
    } catch (error) {
        updateInteractionObject(
            store,
            {
                treeRequestState: RequestStatusType.ERROR
            },
            {
                tree: {
                    ...store.getState().interactions.currentInteraction?.context?.tree,
                    currentNodeId: node.id,
                    currentNodeType: nodeType
                }
            }
        );
        dispatchNotification({ message: 'Erro ao carregar o portfólio do cliente (tree)', severity: 'error' });
    }
};
